import type React from 'react';
import type { FC } from 'react';

import { map } from 'lodash-es';

import VARIANTS from '../../../constants/variants';
import Button from '../../button/button';
import RadioButtonsWrapper from '../../forms/field_wrappers/radio_buttons/radio_buttons';
import SelectFieldWrapper from '../../forms/field_wrappers/select/select';
import { insertDateTime } from '../../input_date/utils';

import styles from './time_picker.module.css';

export type TimeFormatOptions = 'am' | 'pm' | '24h';

const labelWidth = '25px';

const generateTimeOptions = (
  max: number,
  min = 0,
): { value: string; label: string }[] => {
  const options = [];
  for (let i = min; i < max; i += 1) {
    const value = i.toLocaleString(undefined, {
      minimumIntegerDigits: 2,
    });
    options.push({
      value,
      label: value,
    });
  }
  return options;
};

const TIME_FORMAT_OPTIONS: { value: TimeFormatOptions; label: string }[] = [
  { value: 'am', label: 'AM' },
  { value: 'pm', label: 'PM' },
  { value: '24h', label: '24h' },
];

// The dropdown hands back the raw option string.
const toTimeFormat = (
  value: string | undefined,
): TimeFormatOptions | undefined =>
  TIME_FORMAT_OPTIONS.find((option) => option.value === value)?.value;

const HOURS_OPTIONS_12H = generateTimeOptions(13, 1);
const first12hOption = HOURS_OPTIONS_12H.pop();
if (first12hOption) {
  HOURS_OPTIONS_12H.unshift(first12hOption);
}
const HOURS_OPTIONS_24H = generateTimeOptions(24);
const TIME_OPTIONS = generateTimeOptions(60);

type Props = {
  selectedTimeFormat: TimeFormatOptions;
  hours?: string;
  minutes?: string;
  seconds?: string;
  startDate?: Date;
  setTime: (value: string, name: string) => void;
  setTimeFormat: (value: TimeFormatOptions) => void;
  onDone: (startDate: Date, event: React.MouseEvent<HTMLButtonElement>) => void;
};

const TimePicker: FC<Props> = ({
  selectedTimeFormat,
  hours = '12',
  minutes = '00',
  seconds = '00',
  setTime,
  setTimeFormat,
  onDone,
  startDate,
}) => {
  const onTimeSelect = (value?: string, name?: string) => {
    if (!name || !value) {
      return;
    }
    switch (name) {
      case 'hours':
        setTime(value, name);
        break;
      case 'minutes':
        setTime(value, name);
        break;
      case 'seconds':
        setTime(value, name);
        break;
      default:
        break;
    }
  };

  const hoursOptions =
    selectedTimeFormat === '24h' ? HOURS_OPTIONS_24H : HOURS_OPTIONS_12H;

  const selectedHours = map(hoursOptions, 'value').includes(hours)
    ? hours
    : '12';

  return (
    <div aria-label="Time Picker" className={styles.root}>
      <RadioButtonsWrapper
        className={styles.time_format_options}
        name="time_format_options"
        value={selectedTimeFormat}
        onChange={(value) => {
          const timeFormat = toTimeFormat(value);
          if (timeFormat) {
            setTimeFormat(timeFormat);
            sessionStorage.setItem(
              'timePickerTimeFormat',
              timeFormat === '24h' ? '24h' : '12h',
            );
          }
        }}
        options={TIME_FORMAT_OPTIONS}
      />
      <div className={styles.time_selector_wrapper}>
        <div className={styles.time_selector}>
          <SelectFieldWrapper
            name="hours"
            label="Hours"
            options={hoursOptions}
            value={selectedHours}
            vertical
            labelWidth={labelWidth}
            onChange={(value) => onTimeSelect(value, 'hours')}
          />
        </div>
        <span className={styles.time_selector_separator}>:</span>
        <div className={styles.time_selector}>
          <SelectFieldWrapper
            name="minutes"
            label="Minutes"
            options={TIME_OPTIONS}
            value={minutes}
            vertical
            labelWidth={labelWidth}
            onChange={(value) => onTimeSelect(value, 'minutes')}
          />
        </div>
        <span className={styles.time_selector_separator}>:</span>
        <div className={styles.time_selector}>
          <SelectFieldWrapper
            name="seconds"
            label="Seconds"
            options={TIME_OPTIONS}
            value={seconds}
            vertical
            labelWidth={labelWidth}
            onChange={(value) => onTimeSelect(value, 'seconds')}
          />
        </div>
      </div>
      <Button
        variant={VARIANTS.INFO}
        className={styles.done_button}
        onClick={(event) => {
          const newStartDate = insertDateTime(
            startDate ?? new Date(),
            Number(hours),
            Number(minutes),
            Number(seconds),
            selectedTimeFormat,
          );

          onDone(newStartDate, event);
        }}
      >
        Done
      </Button>
    </div>
  );
};

export default TimePicker;
